import { createSlice, createAsyncThunk, ThunkDispatch, AnyAction, PayloadAction } from "@reduxjs/toolkit";
import { saveAs } from "file-saver";
import {
  ApiException,
  EncodingFileDto,
  ErrorResponse,
  IModificationStacksClient,
  IModificationsClient,
  IProjectsDataClient,
  ModificationStacksClient,
  ModificationsClient,
  PollingStatuses,
  ProjectsDataClient} from "app/ApiClient";
import { httpStatus } from "const/httpStatus";

import { tryGetValueFromJson } from "utils/parseHelper";
import { IState } from "..";
import { initialEncodingFilesState, DownLoadFilePayloadType } from "./encodingFilesState";

const projectsDataClient: IProjectsDataClient = new ProjectsDataClient(process.env.REACT_APP_API_URL);
const modificationStackClient: IModificationStacksClient = new ModificationStacksClient(process.env.REACT_APP_API_URL);
const modificationClient: IModificationsClient = new ModificationsClient(process.env.REACT_APP_API_URL);
const SPSS_ERROR_GENERAL = "SPSS generation failed." ;
const SPSS_ERROR_NOT_FOUND = "SPSS not found with selected options";
const CSV_ERROR_GENERAL = "CSV generation failed.";
const CSV_ERROR_NOT_FOUND = "CSV not found with selected options";
const POLLING_INTERVAL_MS = 5000;
const CSV_FILE_NOT_GENERATED = "Csv file not generated.";
const SPSS_FILE_NOT_GENERATED = "SPSS file not generated.";

const encodingFilesSlice = createSlice({
  name: "encodingFiles",
  initialState: initialEncodingFilesState,
  reducers: {
    resetEncodingFilesState: (state) => { 
      const {downloadTokens} = state;
      return {
        ...initialEncodingFilesState,
        downloadTokens : {
          ...downloadTokens
        }
      }
    },
    spssLoadingStarted: (state) => {
      state.isLoadingSpss = true;
      state.spssFailedMessage = null;
      state.spssSucceed = false;
    },
    spssSetFailed: (state, { payload }: { payload: string | null }) => {
      state.spssFailedMessage = payload;
      state.isLoadingSpss = false;
      state.spssSucceed = false;
    },
    spssSetSucceed: (state) => {
      state.spssSucceed = true;
      state.isLoadingSpss = false;
      state.spssFailedMessage = null;
    },
    setSpssDirectDownloadUrl: (state, { payload }: PayloadAction<string>)=>{
      state.spssDownloadUrl = payload;
    },
    csvLoadingStarted: (state) => {
      state.isLoadingCsv = true;
      state.csvFailedMessage = null;
      state.csvSucceed = false;
    },
    csvSetFailed: (state, { payload }: { payload: string | null }) => {
      state.csvFailedMessage = payload;
      state.isLoadingCsv = false;
      state.csvSucceed = false;
    },
    csvSetSucceed: (state) => {
      state.csvSucceed = true;
      state.isLoadingCsv = false;
      state.csvFailedMessage = null;
    },
    setCsvDirectDownloadUrl: (state, { payload }: PayloadAction<string>)=>{
      state.csvDownloadUrl = payload;
    },
    downloadTokenReceived :(state, {payload: { token, projectId, payloadUsed}}: { payload: { token:string, projectId: string, payloadUsed:DownLoadFilePayloadType }})=>{
      state.downloadTokens[`${token}`] ={ projectId, payloadUsed };
    },
    downloadTokenRemoved :(state, {payload: { token}}: { payload: { token:string}})=>{
      delete state.downloadTokens[`${token}`];
    }
  },
});

const fileSetFailedMessage = (
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
  failedMessage: string, isDownloadCsv: boolean) => {
  if(isDownloadCsv) {
    dispatch(csvSetFailed(failedMessage));
  } else {
    dispatch(spssSetFailed(failedMessage));
  }
}

export const {
  csvLoadingStarted,
  csvSetFailed,
  csvSetSucceed,
  spssLoadingStarted,
  spssSetFailed,
  spssSetSucceed,
  setSpssDirectDownloadUrl,
  resetEncodingFilesState,
  downloadTokenReceived, 
  downloadTokenRemoved,
  setCsvDirectDownloadUrl
} = encodingFilesSlice.actions;

export const getSpssFile = createAsyncThunk(
  "encodingFiles/getSpssFile",
  async (
    { archived, stackId, modificationId, zerofill, concatenate, includeEmpty, filters}:
      { archived?: boolean; stackId?: number; modificationId?: number, zerofill: boolean, concatenate: boolean, includeEmpty: boolean, filters:string },
    { dispatch, getState, signal },
  ) => {
    try {
      const {
        project: { currentProject },
        datasetsData: { includePageContext, currentVersionId },
      } = getState() as IState;
      const batteries = includePageContext.filter?.batteries ?? [];

      const projectId = currentProject?.id;
      if (!projectId || !currentVersionId || !currentProject?.originProjectId || !currentProject.name) {
        return;
      }
      dispatch(spssLoadingStarted());
      const payloadUsed : DownLoadFilePayloadType = {};
      let result: Nullable<EncodingFileDto> = null;
      if (stackId) {
        payloadUsed.spssByStack = {
          projectId,
          stackId,
          archived: Boolean(archived),
          encodingVersionId: currentVersionId,
          zerofill,
          includeEmptyVariables: includeEmpty,
          concatenateHeaders : concatenate,
          batteries,
          filters,
        }
        result = await modificationStackClient.createSpssFile(payloadUsed.spssByStack);
      } else if (modificationId) {
        payloadUsed.spssByModification  = {
          projectId,
          modificationId,
          archived: Boolean(archived),
          encodingVersionId: currentVersionId,
          batteries,
          zerofill,
          includeEmptyVariables: includeEmpty,
          concatenateHeaders : concatenate,
          filters,
        }
        result = await modificationClient.createSpssFile(payloadUsed.spssByModification);
      } else {
        payloadUsed.spssByProject = {
          projectId,
          archived: Boolean(archived),
          batteries,
          encodingVersionId: currentVersionId,
          zerofill,
          includeEmptyVariables: includeEmpty,
          concatenateHeaders : concatenate,
          filters,
        };
        result = await projectsDataClient.createSpssFile(payloadUsed.spssByProject);
      }
      if (signal.aborted) {
        return;
      }
      if(result.token){
        dispatch(downloadTokenReceived({
          projectId: currentProject.originProjectId ?? "",
          token: result.token,
          payloadUsed: payloadUsed
        }));
        dispatch(getDownloadStatus({token: result.token, isDownloadCsv: false }));
      }
      else if (result.url) {
        saveAs(result.url);
        dispatch(spssSetSucceed());
      }
    } catch (error) {
      if (ApiException.isApiException(error)) {
        const { status, response, message } = error;
        const failMessage = status === httpStatus.notFound ? SPSS_ERROR_NOT_FOUND : SPSS_ERROR_GENERAL;
        const detailMessage = tryGetValueFromJson((response as ErrorResponse).errorMessage ?? message, "detail");
        dispatch(spssSetFailed(detailMessage ?? failMessage));
        return;
      }
      dispatch(spssSetFailed(""));
    }
  },
);

export const getDatasetSpssFile = createAsyncThunk(
  "encodingFiles/getDatasetSpssFile",
  async (
    { archived, stackId, modificationId, zerofill, concatenate, includeEmpty, filters}:
      { archived?: boolean; stackId?: number; modificationId?: number, zerofill: boolean, concatenate: boolean, includeEmpty: boolean, filters:string | undefined},
    { dispatch, getState, signal },
  ) => {
    try {
      const {
        project: { currentProject },
        datasetsData: { currentVersionId },
      } = getState() as IState;

      const projectId = currentProject?.id;
      if (!projectId || !currentVersionId || !currentProject?.originProjectId || !currentProject.name) {
        return;
      }
      dispatch(spssLoadingStarted());
      const payloadUsed : DownLoadFilePayloadType = {};
      let result: Nullable<EncodingFileDto> = null;
      if (stackId) {
        payloadUsed.datasetSpssByStack = {
          projectId,
          stackId,
          archived: Boolean(archived),
          encodingVersionId: currentVersionId,
          zerofill,
          includeEmptyVariables: includeEmpty,
          concatenateHeaders : concatenate,
          filters,
        }
        result = await modificationStackClient.createDatasetSpssFile(payloadUsed.datasetSpssByStack)
      } else if (modificationId) {
        payloadUsed.datasetSpssByModification  = {
          projectId,
          modificationId,
          archived: Boolean(archived),
          encodingVersionId: currentVersionId,
          zerofill,
          includeEmptyVariables: includeEmpty,
          concatenateHeaders : concatenate,
          filters,
        }
        result = await modificationClient.createDatasetSpssFile(payloadUsed.datasetSpssByModification);
      } else {
        payloadUsed.datasetSpssByProject = {
          projectId,
          archived: Boolean(archived),
          encodingVersionId: currentVersionId,
          zerofill,
          includeEmptyVariables: includeEmpty,
          concatenateHeaders : concatenate,
          filters,
        }
        result = await projectsDataClient.createDatasetSpssFile(payloadUsed.datasetSpssByProject);
      }
      if (signal.aborted) {
        return;
      }
      if(result.token){
        dispatch(downloadTokenReceived({
          projectId: currentProject.originProjectId ?? "", 
          token: result.token, 
          payloadUsed: payloadUsed
        }));
        dispatch(getDownloadStatus({token: result.token, isDownloadCsv: false }));
      }
      else if (result.url) {
        dispatch(setSpssDirectDownloadUrl(result.url));
        saveAs(result.url);
        dispatch(spssSetSucceed());
      }
    } catch (error) {
      if (ApiException.isApiException(error)) {
        const { status, response, message } = error;
        const failMessage = status === httpStatus.notFound ? SPSS_ERROR_NOT_FOUND : SPSS_ERROR_GENERAL;
        const detailMessage = tryGetValueFromJson((response as ErrorResponse).errorMessage ?? message, "detail");
        dispatch(spssSetFailed(detailMessage ?? failMessage));
        return;
      }
      dispatch(spssSetFailed(""));
    }
  },
);

export const getDownloadStatus = createAsyncThunk(
  "encodingFiles/getDownloadStatus",
  async (
   { token, isDownloadCsv }: {token: string, isDownloadCsv: boolean},
    { dispatch, getState },
  ) => {
    try {

      const { encodingFiles: { downloadTokens} } = getState() as IState
      const { projectId, payloadUsed } = downloadTokens[`${token}`];
      if(!projectId && !payloadUsed)
        return;

      const result = await projectsDataClient.getEncodedDataDownloadStatus(token?? "" , projectId ?? "");
      switch(result?.status)
      {
        case PollingStatuses.Complete : 
          dispatch(downloadCsvFile({ token, payloadUsed}));
          break;
        case PollingStatuses.InProgress:
          setTimeout(()=>{
            dispatch(getDownloadStatus({token, isDownloadCsv}));
          }, POLLING_INTERVAL_MS);
          break;
        default:
          const failedMessage = isDownloadCsv ? CSV_FILE_NOT_GENERATED : SPSS_FILE_NOT_GENERATED;
          fileSetFailedMessage(dispatch, failedMessage, isDownloadCsv);
          break;
      }
      
    } catch (error) {
      if (ApiException.isApiException(error)) {
        const { status, response, message } = error;
        const failCsvMessage = status === httpStatus.notFound ? CSV_ERROR_NOT_FOUND : CSV_ERROR_GENERAL;
        const failSpssMessage = status === httpStatus.notFound ? SPSS_ERROR_NOT_FOUND : SPSS_ERROR_GENERAL;
        const detailMessage = tryGetValueFromJson((response as ErrorResponse).errorMessage ?? message, "detail");
        const failedMessage = isDownloadCsv ? failCsvMessage : failSpssMessage;
        fileSetFailedMessage(dispatch, detailMessage ?? failedMessage, isDownloadCsv);
        return;
      }
      fileSetFailedMessage(dispatch, "", isDownloadCsv);
    }
  },
);

export const getCsvFile = createAsyncThunk(
  "encodingFiles/getCsvFile",
  async (
    { modificationId, stackId, zerofill }: { modificationId?: number; stackId?: number, zerofill: boolean},
    { dispatch, getState, signal },
  ) => {
    try {
      const {
        project: { currentProject },
        datasetsData: { includePageContext, currentVersionId },
      } = getState() as IState;

      const batteries = includePageContext.filter?.batteries ?? [];

      const projectId = currentProject?.id;

      if (!projectId || !currentVersionId || !currentProject?.originProjectId || !currentProject?.name) {
        return;
      }
      dispatch(csvLoadingStarted());
      let result: Nullable<EncodingFileDto> = null;
      const payloadUsed : DownLoadFilePayloadType = {};
      if (stackId) {
        payloadUsed.csvByStack = {
          encodingVersionId: currentVersionId,
          projectId,
          stackId,
          zerofill,
          batteries,
        };
        result = await modificationStackClient.createCsvFile(payloadUsed.csvByStack);
      } else if (modificationId) {
        payloadUsed.csvByModification = {
          encodingVersionId: currentVersionId,
          projectId,
          modificationId,
          batteries,
          zerofill,
        }
        result = await modificationClient.createCsvFile(payloadUsed.csvByModification);
      } else {
        payloadUsed.csvByProject = {
          projectId,
          batteries,
          encodingVersionId: currentVersionId,
          zerofill,
        }
        result = await projectsDataClient.createCsvFile(payloadUsed.csvByProject);
      }
      if (signal.aborted) {
        return;
      }
      if (result.token){
          dispatch(downloadTokenReceived({ token: result.token, projectId: currentProject.originProjectId, payloadUsed}));
        dispatch(getDownloadStatus({token: result.token, isDownloadCsv: true }));
      }
      else if (result.url) {
        setCsvDirectDownloadUrl(result.url);
        saveAs(result.url);
        dispatch(csvSetSucceed());
      }
    } catch (error) {
      if (ApiException.isApiException(error)) {
        const { status, response, message } = error;
        const failMessage = status === httpStatus.notFound ? CSV_ERROR_NOT_FOUND : CSV_ERROR_GENERAL;
        const detailMessage = tryGetValueFromJson((response as ErrorResponse).errorMessage ?? message, "detail");
        dispatch(csvSetFailed(detailMessage ?? failMessage));
        return;
      }
      dispatch(csvSetFailed(""));
    }
  },
);

export const getDatasetCsvFile = createAsyncThunk(
  "encodingFiles/getDatasetCsvFile",
  async (
    { modificationId, stackId, zerofill, filters }: { modificationId?: number; stackId?: number, zerofill: boolean, filters: string | undefined },
    { dispatch, getState, signal },
  ) => {
    try {
      const {
        project: { currentProject },
        datasetsData: { currentVersionId },
      } = getState() as IState;

      const projectId = currentProject?.id;

      if (!projectId || !currentVersionId || !currentProject?.originProjectId || !currentProject?.name) {
        return;
      }
      dispatch(csvLoadingStarted());
      let result: Nullable<EncodingFileDto> = null;
      const payloadUsed : DownLoadFilePayloadType = {};
      if (stackId) {
        payloadUsed.datasetCsvByStack = {
          encodingVersionId: currentVersionId,
          projectId,
          stackId,
          zerofill,
          filters,
        }
        result = await modificationStackClient.createDatasetCsvFile(payloadUsed.datasetCsvByStack);
      } else if (modificationId) {
        payloadUsed.datasetCsvByModification = {
          encodingVersionId: currentVersionId,
          projectId,
          modificationId,
          zerofill,
          filters,
        }
        result = await modificationClient.createDatasetCsvFile(payloadUsed.datasetCsvByModification);
      } else {
        payloadUsed.datasetCsvByProject = {
          projectId,
          encodingVersionId: currentVersionId,
          zerofill,
          filters,
        }
        result = await projectsDataClient.createDatasetCsvFile(payloadUsed.datasetCsvByProject);
      }
      if (signal.aborted) {
        return;
      }
      if (result.token) {
        dispatch(downloadTokenReceived({ token: result.token, projectId: currentProject.originProjectId, payloadUsed}));
        dispatch(getDownloadStatus({token: result.token, isDownloadCsv: true }));
      }
      else if (result.url) {
        saveAs(result.url);
        dispatch(csvSetSucceed());
      }
      
    } catch (error) {
      if (ApiException.isApiException(error)) {
        const { status, response, message } = error;
        const failMessage = status === httpStatus.notFound ? CSV_ERROR_NOT_FOUND : CSV_ERROR_GENERAL;
        const detailMessage = tryGetValueFromJson((response as ErrorResponse).errorMessage ?? message, "detail");
        dispatch(csvSetFailed(detailMessage ?? failMessage));
        return;
      }
      dispatch(csvSetFailed(""));
    }
  },
);

export const downloadCsvFile = createAsyncThunk(
  "encodingFiles/downloadCsvFile",
  async (
    { 
        token,
        payloadUsed : { 
          csvByModification, 
          csvByProject, 
          csvByStack, 
          spssByModification, 
          spssByProject, 
          spssByStack,
          datasetCsvByModification,
          datasetCsvByProject,
          datasetCsvByStack,
          datasetSpssByModification,
          datasetSpssByProject,
          datasetSpssByStack,
        }
    }:{ token: string, payloadUsed: DownLoadFilePayloadType },
    { dispatch, signal },
  ) => {
    const isDownloadCsv = (csvByStack || csvByModification ||  csvByProject || datasetCsvByStack || datasetCsvByModification || datasetCsvByProject);
    try {
      let result: Nullable<EncodingFileDto> = null;
      if(isDownloadCsv){
        dispatch(csvLoadingStarted());
      } else {
        dispatch(spssLoadingStarted())
      }

      if (csvByStack) {
        result = await modificationStackClient.createCsvFile(csvByStack);
      } else if (csvByModification) {
        result = await modificationClient.createCsvFile(csvByModification);
      } else if(csvByProject) {
        result = await projectsDataClient.createCsvFile(csvByProject);
      } else if (spssByStack) {
        result = await modificationStackClient.createSpssFile(spssByStack);
      } else if (spssByModification) {
        result = await modificationClient.createSpssFile(spssByModification);
      } else if (spssByProject) {
        result = await projectsDataClient.createSpssFile(spssByProject);
      } else if(datasetCsvByStack) {
        result = await modificationStackClient.createDatasetCsvFile(datasetCsvByStack);
      } else if(datasetCsvByModification) {
        result = await modificationClient.createDatasetCsvFile(datasetCsvByModification);
      } else if(datasetCsvByProject) {
        result = await projectsDataClient.createDatasetCsvFile(datasetCsvByProject);
      } else if(datasetSpssByStack) {
        result = await modificationStackClient.createDatasetSpssFile(datasetSpssByStack);
      } else if(datasetSpssByModification) {
        result = await modificationClient.createDatasetSpssFile(datasetSpssByModification);
      } else if(datasetSpssByProject) {
        result = await projectsDataClient.createDatasetSpssFile(datasetSpssByProject);
      }
      if (signal.aborted) {
        return;
      }
      dispatch(downloadTokenRemoved({token}))
      if (result?.url) {
        saveAs(result.url);
        if(isDownloadCsv){
          dispatch(setCsvDirectDownloadUrl(result.url));
          dispatch(csvSetSucceed());
        } else {
          dispatch(setSpssDirectDownloadUrl(result.url));
          dispatch(spssSetSucceed())
        }
      }
      else {
        const failedMessage = isDownloadCsv ? CSV_FILE_NOT_GENERATED : SPSS_FILE_NOT_GENERATED;
        fileSetFailedMessage(dispatch, failedMessage, isDownloadCsv ? true : false);
        return;
      }
    } catch (error) {
      if (ApiException.isApiException(error)) {
        const { status, response, message } = error;
        const failCsvMessage = status === httpStatus.notFound ? CSV_ERROR_NOT_FOUND : CSV_ERROR_GENERAL;
        const failSpssMessage = status === httpStatus.notFound ? SPSS_ERROR_NOT_FOUND : SPSS_ERROR_GENERAL;
        const detailMessage = tryGetValueFromJson((response as ErrorResponse).errorMessage ?? message, "detail");
        const failedMessage = isDownloadCsv ? failCsvMessage : failSpssMessage;
        fileSetFailedMessage(dispatch, detailMessage ?? failedMessage, isDownloadCsv ? true : false);
        return;
      }
      fileSetFailedMessage(dispatch, "", isDownloadCsv ? true : false);
    }
  },
);

export default encodingFilesSlice.reducer;
